import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import TopBar from './top-bar';
import LeftSheet from './left-sheet';
import RightSheet from './right-sheet';
import Chat from './chat';
import TelemetryLogo from './logo';
import type { Message, AppSettings, Venue, PendingAction } from '@/src/lib/types';
import { useAuth } from '@/src/hooks/useAuth';
import { useCurrentUser } from '@/src/hooks/useCurrentUser';
import { usePendingActions } from '@/src/hooks/usePendingActions';
import { useNotifications } from '@/src/hooks/useNotifications';
import { useChatHistory } from '@/src/hooks/useChatHistory';
import { REQUEST_KINDS } from './right-sheet';
import { useEodReports } from '@/src/hooks/useEodReports';
import { useChat } from '@/src/hooks/useChat';
import { useOrgId } from '@/src/hooks/useOrgId';
import { useVenueContext } from '@/src/hooks/useVenueContext';
import { useDrawerContexts } from '@/src/hooks/useDrawerContexts';
import { supabase } from '@/src/lib/supabase';
import { log, track } from '@/src/lib/logger';
import { sfx } from '@/src/lib/sfx';
import * as Sentry from '@sentry/react';
import { X } from 'lucide-react';
import OverviewPanel from './overview-panel';
import StaffDashboard from './staff-dashboard';
import FinancialsPage, { type FinTab } from './financials-page';
import type { SettingsSection } from './settings-page';
const SettingsPage = lazy(() => import('./settings-page'));
const TeamChatsPanel = lazy(() => import('@/src/components/team/team-chats-panel'));
import { useInvoiceAccess } from '@/src/lib/invoices';
import { useTeamUnread } from '@/src/lib/teamChats';
import type { TaskPreset } from '@/src/hooks/useTasks';
import NotificationPrompt from './notification-prompt';
import DocumentsPage from './documents-page';
import TasksPage from './tasks-page';
import { pushToast } from '@/src/hooks/useToasts';
import TrainingPage from './training-page';
import EodSection, { type EodView } from './eod-section';
import { useAppColors } from '@/src/lib/theme';
import LogoMark from '@/src/components/LogoMark';
import BootScreen from '@/src/components/boot-screen';
import NoActiveVenue from './no-active-venue';

// Look-up tools: never need an "already done" note in chat history.
const READ_ONLY_TOOLS = new Set(['web_search', 'search_past_chats', 'save_memory', 'forget_memory', 'get_document', 'list_documents', 'get_roster', 'get_deputy_roster', 'get_stock', 'get_low_stock', 'get_suppliers', 'get_insights', 'get_price_comparison', 'get_square_summary', 'get_cogs_report', 'list_users', 'get_deputy_employees', 'generate_daily_report', 'list_my_shifts', 'list_my_tasks', 'get_open_tasks', 'list_wastage', 'list_recipes', 'get_my_training', 'get_training_results', 'list_pending_actions', 'list_my_requests', 'get_venue_numbers', 'get_labour_breakdown']);

// Shown next to the typing dots while the assistant uses a tool.
const TOOL_STATUS: Record<string, string> = {
  web_search: 'Searching the web…',
  save_memory: 'Updating memory…',
  forget_memory: 'Updating memory…',
  save_venue_memory: 'Updating venue memory…',
  forget_venue_memory: 'Updating venue memory…',
  search_past_chats: 'Looking through your chats…',
  create_quiz: 'Writing the quiz… this can take up to a minute',
  get_document: 'Reading the document…',
  list_documents: 'Looking at your documents…',
  list_users: 'Checking the team…',
  get_deputy_employees: 'Checking the team…',
  get_roster: 'Checking the roster…',
  get_deputy_roster: 'Checking the roster…',
  get_stock: 'Checking stock…',
  get_low_stock: 'Checking stock…',
  get_venue_numbers: 'Pulling the numbers…',
  generate_daily_report: 'Putting the report together…',
};

const MemoryModal = lazy(() => import('./memory-modal'));
const AuditLogModal = lazy(() => import('./audit-log-modal'));
const ApprovalRulesModal = lazy(() => import('./approval-rules-modal'));
const TasksModal = lazy(() => import('./tasks-modal'));
const NotificationsSheet = lazy(() => import('./notifications-sheet'));
import TranslationHost from '@/src/components/translation-host';
const FileViewerHost = lazy(() => import('./pdf-pages').then((m) => ({ default: m.FileViewerHost })));
const ToolPermissionsModal = lazy(() => import('./tool-permissions-modal'));
const FilesModal = lazy(() => import('./files-modal'));
const StockModal = lazy(() => import('./stock-modal'));
const RosterModal = lazy(() => import('./roster-modal'));
const WastageModal = lazy(() => import('./wastage-modal'));
const PurchaseOrdersModal = lazy(() => import('./purchase-orders-modal'));
const StockCountModal = lazy(() => import('./stock-count-modal'));
const InvoiceScanModal = lazy(() => import('./invoice-scan-modal'));
const RecipesModal = lazy(() => import('./recipes-modal'));
const DailyReportModal = lazy(() => import('./daily-report-modal'));
const CogsModal = lazy(() => import('./cogs-modal'));
const ContextModal = lazy(() => import('./context-modal'));
const InviteModal = lazy(() => import('./invite-modal'));
const TeamAccessModal = lazy(() => import('./team-access-modal'));
const EodQuestionsModal = lazy(() => import('./eod-questions-modal'));
const PriceComparisonModal = lazy(() => import('./price-comparison-modal'));
const OnboardingHost = lazy(() => import('@/src/components/onboarding/onboarding-host'));

export default function Shell() {
  const c = useAppColors();
  const { session, signOut } = useAuth();
  const authUserId = session?.user?.id ?? null;
  const { user, venue, loading: profileLoading, error: profileError } = useCurrentUser(authUserId);
  const { actions: dbActions } = usePendingActions(venue?.id ?? null);
  const { items: notifItems, markRead: markNotifIdsRead, clear: clearNotifs } = useNotifications(user?.id ?? null);
  const isManagerUser = user?.role === 'owner' || user?.role === 'manager';
  // Staff inbox: updates on their own requests; badge = unread ones.
  const requestNotifs = notifItems.filter((n) => REQUEST_KINDS.has(n.kind));
  const unreadRequestIds = requestNotifs.filter((n) => !n.read_at && n.kind !== 'request_sent').map((n) => n.id);
  // Bell = everything except request/approval traffic, which lives in the inbox.
  const isInboxKind = (kind: string) => REQUEST_KINDS.has(kind) || kind === 'approval_request';
  const bellUnreadIds = notifItems.filter((n) => !n.read_at && !isInboxKind(n.kind)).map((n) => n.id);
  const { eodQuestions, saveEodQuestions } = useEodReports(venue?.id ?? null, user?.id ?? null);
  const { orgId } = useOrgId(venue?.id ?? null);
  const { context: venueContext, saving: venueContextSaving, save: saveVenueContext } = useVenueContext(venue?.id ?? null);
  const {
    contexts: drawerContexts,
    filesByDrawer,
    saving: drawerContextSaving,
    saveDrawer: saveDrawerContext,
    loadFiles: loadDrawerFiles,
    uploadFile: uploadDrawerFile,
    deleteFile: deleteDrawerFile,
  } = useDrawerContexts(venue?.id ?? null);
  const { send: sendChat } = useChat();

  const [isScrolled, setIsScrolled] = useState(false);
  const [isLeftOpen, setIsLeftOpen] = useState(false);
  const [isRightOpen, setIsRightOpen] = useState(false);
  const [isMemoryOpen, setIsMemoryOpen] = useState(false);
  // A task's link button asks to open a page (a quiz in Training, the EOD report...).
  const [openCourseId, setOpenCourseId] = useState<string | null>(null);
  const [openDocId, setOpenDocId] = useState<string | null>(null);
  const [pdfEdit, setPdfEdit] = useState<{ bucket: string; name: string; changes: Array<{ find: string; replace: string; near?: string }> } | null>(null);
  const [taskPreset, setTaskPreset] = useState<TaskPreset | null>(null);
  useEffect(() => {
    const go = (e: Event) => {
      const d = (e as CustomEvent<{ page?: string; courseId?: string; docId?: string; view?: 'submit' | 'numbers' | 'team' | 'hub'; preset?: TaskPreset; pdfEdit?: { bucket: string; name: string; changes: Array<{ find: string; replace: string; near?: string }> } }>).detail ?? {};
      setIsTasksOpen(false);
      setIsLeftOpen(false);
      if (d.page === 'training') { setOpenCourseId(d.courseId ?? null); setActiveDrawerId('training'); }
      if (d.page === 'eod') { setEodRequest({ view: d.view === 'numbers' ? 'numbers' : d.view === 'team' ? 'team' : d.view === 'hub' ? 'hub' : 'checkin', n: Date.now() }); setActiveDrawerId('eod'); }
      if (d.page === 'integrations') setActiveDrawerId('integrations');
      if (d.page === 'documents') { if (d.docId) setOpenDocId(d.docId); if (d.pdfEdit) setPdfEdit(d.pdfEdit); setActiveDrawerId('documents'); }
      if (d.page === 'numbers') setActiveDrawerId('numbers');
      if (d.page === 'invoices') setActiveDrawerId('invoices');
      if (d.page === 'teamchats') setActiveDrawerId('teamchats');
      if (d.page === 'chat') setActiveDrawerId('chat');
      if (d.page === 'tasks') { if (d.preset) setTaskPreset(d.preset); setActiveDrawerId('tasks'); }
      if (d.page === 'dashboard') setActiveDrawerId('dashboard');
      if (d.page === 'team') setIsTeamAccessOpen(true);
      if (d.page === 'inbox') setIsRightOpen(true);
    };
    window.addEventListener('opsalo:navigate', go);
    return () => window.removeEventListener('opsalo:navigate', go);
  }, []);
  // The tour opens and closes the phone sidebar to point at things in it.
  useEffect(() => {
    const on = (e: Event) => setIsLeftOpen(!!(e as CustomEvent<{ open?: boolean }>).detail?.open);
    window.addEventListener('opsalo:sidebar', on);
    return () => window.removeEventListener('opsalo:sidebar', on);
  }, []);
  const [memoryTab, setMemoryTab] = useState<'personal' | 'venue'>('personal');
  // "Memory updated" under a reply opens the memory screen on the right tab.
  useEffect(() => {
    const open = (e: Event) => { setMemoryTab((e as CustomEvent<{ tab?: 'personal' | 'venue' }>).detail?.tab ?? 'personal'); setIsMemoryOpen(true); };
    window.addEventListener('opsalo:open-memory', open);
    return () => window.removeEventListener('opsalo:open-memory', open);
  }, []);
  const [isTasksOpen, setIsTasksOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState<Set<string> | null>(null); // unread ids at the moment the bell was opened
  const [eodRequest, setEodRequest] = useState<{ view: EodView; n: number } | null>(null);
  const [isApprovalRulesOpen, setIsApprovalRulesOpen] = useState(false);
  const [isAuditLogOpen, setIsAuditLogOpen] = useState(false);
  const [isToolPermissionsOpen, setIsToolPermissionsOpen] = useState(false);
  const [isFilesOpen, setIsFilesOpen] = useState(false);
  const [integrationResult, setIntegrationResult] = useState<{ provider: string; result: string; message: string | null } | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const provider = params.get('integration');
    if (!provider) return;
    setIntegrationResult({ provider, result: params.get('result') ?? '', message: params.get('message') });
    setActiveDrawerId('integrations');
    window.history.replaceState(null, '', window.location.pathname);
  }, []);

  // Opened at an in-app link (e.g. a #pdfedit/… or #doc/… link opened in a new tab): go there.
  useEffect(() => {
    const h = window.location.hash;
    if (!user || !venue || !/^#(pdfedit|doc)\//.test(h)) return;
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    setTimeout(() => {
      if (h.startsWith('#doc/')) { window.dispatchEvent(new CustomEvent('opsalo:navigate', { detail: { page: 'documents', docId: h.slice(5) } })); return; }
      try {
        const raw = h.slice(9).replace(/-/g, '+').replace(/_/g, '/');
        const d = JSON.parse(decodeURIComponent(escape(atob(raw + '==='.slice((raw.length + 3) % 4))))) as { b: string; n: string; c: Array<{ f: string; r: string; near?: string }> };
        window.dispatchEvent(new CustomEvent('opsalo:navigate', { detail: { page: 'documents', pdfEdit: { bucket: d.b, name: d.n, changes: d.c.map((x) => ({ find: x.f, replace: x.r, near: x.near })) } } }));
      } catch { /* not a valid link */ }
    }, 300);
  }, [user?.id, venue?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tapping a push notification opens the screen it points to.
  useEffect(() => {
    const onOpen = (e: Event) => { const id = (e as CustomEvent<string>).detail; if (id) setActiveDrawerId(id); };
    window.addEventListener('opsalo:open-drawer', onOpen);
    return () => window.removeEventListener('opsalo:open-drawer', onOpen);
  }, []);
  const [isStockOpen, setIsStockOpen] = useState(false);
  const [isRosterOpen, setIsRosterOpen] = useState(false);
  const [isWastageOpen, setIsWastageOpen] = useState(false);
  const [isPOOpen, setIsPOOpen] = useState(false);
  const [isCountOpen, setIsCountOpen] = useState(false);
  const [isScanOpen, setIsScanOpen] = useState(false);
  const [isRecipesOpen, setIsRecipesOpen] = useState(false);
  const [isDailyReportOpen, setIsDailyReportOpen] = useState(false);
  const [isCogsOpen, setIsCogsOpen] = useState(false);
  const [isContextOpen, setIsContextOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isTeamAccessOpen, setIsTeamAccessOpen] = useState(false);
  const [isEodQuestionsOpen, setIsEodQuestionsOpen] = useState(false);
  const [isPriceOpen, setIsPriceOpen] = useState(false);
  const [activeDrawerId, setActiveDrawerId] = useState('chat');
  const [finTab, setFinTab] = useState<FinTab>('overview');
  const [settingsSection, setSettingsSection] = useState<SettingsSection>('profile');
  const [teamOpen, setTeamOpen] = useState(false);
  const [teamExpanded, setTeamExpanded] = useState(false);
  const lastPage = useRef('chat');
  // Old page names now live inside Financials / Settings, and Team Chats is a panel.
  useEffect(() => {
    const id = activeDrawerId;
    if (id === 'numbers' || id === 'invoices') { setFinTab(id === 'numbers' ? 'overview' : 'invoices'); setActiveDrawerId('financials'); return; }
    if (id === 'integrations') { setSettingsSection('integrations'); setActiveDrawerId('settings'); return; }
    if (id === 'teamchats') { setTeamOpen(true); setActiveDrawerId(lastPage.current); return; }
    lastPage.current = id;
  }, [activeDrawerId]);
  const canInvoices = useInvoiceAccess(venue?.id ?? null, user?.id ?? null, user?.role ?? '');
  const teamUnread = useTeamUnread(venue?.id ?? null, user?.id ?? null);
  const [messagesByContext, setMessagesByContext] = useState<Record<string, Message[]>>({});
  // Saved chats (private to this person). currentChatId = the saved chat shown in the main chat.
  const chatHistory = useChatHistory(venue?.id ?? null, user?.id ?? null);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  // The chat being created for this conversation, so two quick messages don't make two chats.
  const newChatRef = useRef<Promise<string | null> | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingStatus, setStreamingStatus] = useState<string | null>(null);
  const [dismissedActionIds, setDismissedActionIds] = useState<Set<string>>(new Set());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const abortStreamRef = useRef<{ cancelled: boolean } | null>(null);

  const [settings, setSettings] = useState<AppSettings>({
    isChaosMemeEnabled: false,
    chaosLevel: 0.5,
    venueName: 'OpsAlo',
    ownerName: '—',
  });

  const [bootDelayDone, setBootDelayDone] = useState(false);
  const startupChimedRef = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setBootDelayDone(true), 1200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (venue) setSettings((s) => ({ ...s, venueName: venue.name }));
  }, [venue]);

  useEffect(() => {
    if (user) {
      setSettings((s) => ({ ...s, ownerName: user.name }));
      Sentry.setUser({ id: user.id, segment: user.role });
      Sentry.setTag('venue_id', user.venueId);
      Sentry.setTag('role', user.role);
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  useEffect(() => {
    if (bootDelayDone && user && venue && !startupChimedRef.current) {
      startupChimedRef.current = true;
      sfx.startup();
    }
  }, [bootDelayDone, user, venue]);

  useEffect(() => {
    if (activeDrawerId === 'chat') {
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }
      }, 400);
    }
  }, [activeDrawerId]);

  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      setIsScrolled(scrollContainerRef.current.scrollTop > 10);
    }
  }, []);

  useEffect(() => {
    const ref = scrollContainerRef.current;
    ref?.addEventListener('scroll', handleScroll);
    return () => ref?.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

// Other pages (e.g. a document's "Ask about it") start a new chat with a question.
const [pendingAsk, setPendingAsk] = useState<string | null>(null);
// The chat handlers are defined further down (after the loading returns), so read them through a ref.
const askHandlers = useRef<{ newChat?: () => void; send?: (t: string) => Promise<void> }>({});

useEffect(() => {
  const onAsk = (e: Event) => {
    const text = (e as CustomEvent<{ text?: string }>).detail?.text?.trim();
    if (!text) return;
    askHandlers.current.newChat?.();
    setPendingAsk(text);
    setActiveDrawerId('chat');
  };
  window.addEventListener('opsalo:ask', onAsk);
  return () => window.removeEventListener('opsalo:ask', onAsk);
}, []);

useEffect(() => {
  if (pendingAsk && activeDrawerId === 'chat' && !isStreaming) {
    const t = pendingAsk;
    if (!askHandlers.current.send) return;
    setPendingAsk(null);
    void askHandlers.current.send(t);
  }
}, [pendingAsk, activeDrawerId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!profileLoading && bootDelayDone && user && !venue && !profileError) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center gap-4 text-center px-6" style={{ background: c.bg }}>
        <LogoMark size={48} color={c.text} />
        <p className="text-sm" style={{ color: c.text }}>You don't currently have access to a venue.</p>
        <p className="text-xs max-w-xs" style={{ color: c.textFaint }}>Ask your venue owner to send you a new invite.</p>
        <button onClick={() => signOut()} className="text-xs font-bold uppercase tracking-widest mt-2" style={{ color: c.accentSoft }}>Sign out</button>
      </div>
    );
  }

  if (!profileLoading && bootDelayDone && user && !venue && !profileError) {
    return <NoActiveVenue onDone={() => window.location.reload()} onSignOut={() => signOut()} />;
  }

  const stillLoading = !bootDelayDone || profileLoading || !user || !venue;

  if (stillLoading) {
    return (
      <BootScreen label={venue?.name ? `Loading ${venue.name}` : 'Loading'}>
        {profileError && (
          <p className="mt-6 text-[11px] text-red-400 max-w-xs text-center px-6">{profileError}</p>
        )}
      </BootScreen>
    );
  }

  const visibleActions: PendingAction[] = dbActions.filter((a) => !dismissedActionIds.has(a.id));
  const currentMessages = messagesByContext['general'] || [];
  const venueForChildren: Venue = {
    id: venue.id,
    name: venue.name,
    ownerName: user.name,
    timezone: venue.timezone,
    state: venue.state ?? null,
  };

  const handleSendMessage = async (text: string) => {
    const userMsgId = Math.random().toString(36).substring(7);
    const assistantMsgId = Math.random().toString(36).substring(7);
    const drawerAtSend = activeDrawerId === 'chat' || activeDrawerId === 'dashboard' || activeDrawerId === 'numbers' ? 'general' : activeDrawerId;
    const venueNameAtSend = venue.name;

    const userMsg: Message = { id: userMsgId, role: 'user', content: text, timestamp: new Date() };
    const assistantMsg: Message = { id: assistantMsgId, role: 'assistant', content: '', timestamp: new Date() };

    // Only real exchanges go back to the AI: skip empty bubbles and error notices.
    const priorHistory = (messagesByContext[drawerAtSend] || [])
      .filter((m) => (m.role === 'user' || m.role === 'assistant') && typeof m.content === 'string' && m.content.trim() && !m.isError)
      .slice(-10)
      .map((m) => {
        // Tell the AI which actions a past reply already carried out, so it never repeats them.
        const done = (m.toolEvents ?? []).filter((e) => !READ_ONLY_TOOLS.has(e.name) && e.name !== 'suggest_actions');
        const note = m.role === 'assistant' && done.length > 0
          ? `\n\n[Already done in this reply, do not repeat: ${done.map((e) => `${e.name}${e.pending ? ' (queued for approval)' : e.ok ? ' (completed)' : ' (failed)'}`).join(', ')}]`
          : '';
        return { role: m.role as 'user' | 'assistant', content: m.content + note };
      });

    setMessagesByContext((prev) => ({
      ...prev,
      [drawerAtSend]: [...(prev[drawerAtSend] || []), userMsg, assistantMsg],
    }));
    setIsStreaming(true);
    sfx.send();
    track('chat', 'send', { drawer: drawerAtSend, len: text.length });

    const abortToken = { cancelled: false };
    abortStreamRef.current = abortToken;

    let receivedAnyText = false;
    let chimePlayed = false;

    // Save to chat history in the background (main chat only); never delays the reply.
    let replyText = '';
    let replyTools: NonNullable<Message['toolEvents']> = [];
    let replySuggestions: Message['suggestions'];
    const saveTo: Promise<string | null> = drawerAtSend !== 'general'
      ? Promise.resolve(null)
      : (currentChatId ? Promise.resolve(currentChatId) : (newChatRef.current ??= chatHistory.create(text).then((id) => { if (id) setCurrentChatId(id); return id; })));
    void saveTo.then((id) => { if (id) void chatHistory.append(id, { role: 'user', content: text }); });
    const saveReply = () => {
      if (!replyText.trim()) return;
      void saveTo.then((id) => { if (id) void chatHistory.append(id, { role: 'assistant', content: replyText, toolEvents: replyTools, suggestions: replySuggestions }); });
    };

    // Wait briefly for a brand-new chat's id (normally a few hundred ms) so the AI's past-chat search skips this chat.
    const conversationId = drawerAtSend === 'general'
      ? await Promise.race([saveTo, new Promise<null>((r) => setTimeout(() => r(null), 1500))])
      : null;

    await sendChat({
      message: text,
      drawerId: drawerAtSend,
      conversationId,
      venueName: venueNameAtSend,
      history: priorHistory,
      onChunk: (chunk) => {
        if (abortToken.cancelled) return;
        if (chunk.type === 'text') replyText += chunk.text;
        if (chunk.type === 'suggestions') replySuggestions = chunk.items;
        if (chunk.type === 'tool_result') replyTools = [...replyTools, { name: chunk.name, ok: chunk.ok, pending: !!chunk.pending }];
        if (chunk.type === 'done' || chunk.type === 'error') saveReply();
        if (chunk.type === 'tool_use') {
          setStreamingStatus(TOOL_STATUS[chunk.name] ?? 'Checking…');
        } else if (chunk.type === 'suggestions') {
          setMessagesByContext((prev) => {
            const list = prev[drawerAtSend] || [];
            return {
              ...prev,
              [drawerAtSend]: list.map((m) => (m.id === assistantMsgId ? { ...m, suggestions: chunk.items } : m)),
            };
          });
        } else if (chunk.type === 'text') {
          setStreamingStatus(null);
          receivedAnyText = true;
          setMessagesByContext((prev) => {
            const list = prev[drawerAtSend] || [];
            return {
              ...prev,
              [drawerAtSend]: list.map((m) =>
                m.id === assistantMsgId ? { ...m, content: m.content + chunk.text } : m,
              ),
            };
          });
        } else if (chunk.type === 'tool_result') {
          track('tool', chunk.name, { ok: chunk.ok, pending: !!chunk.pending, drawer: drawerAtSend });
          const prettyName = chunk.name.replace(/_/g, ' ');
          const readTools = ['get_document', 'get_documents', 'get_roster', 'get_deputy_roster', 'get_stock', 'get_suppliers', 'get_insights', 'get_price_comparison', 'get_square_summary', 'get_cogs_report', 'list_users', 'get_deputy_employees', 'generate_daily_report'];
          const isReadTool = readTools.some(t => chunk.name.startsWith(t));
          if (chunk.pending) {
            pushToast({ kind: 'pending', title: 'Queued for approval', body: prettyName });
          } else if (chunk.ok && !isReadTool) {
            pushToast({ kind: 'success', title: 'Action complete', body: prettyName });
          } else {
            if (!isReadTool) pushToast({ kind: 'error', title: 'Tool failed', body: prettyName });
          }
          setMessagesByContext((prev) => {
            const list = prev[drawerAtSend] || [];
            return {
              ...prev,
              [drawerAtSend]: list.map((m) =>
                m.id === assistantMsgId
                  ? { ...m, toolEvents: [...(m.toolEvents ?? []), { name: chunk.name, ok: chunk.ok, pending: !!chunk.pending }] }
                  : m,
              ),
            };
          });
        } else if (chunk.type === 'done') {
          setStreamingStatus(null);
          setIsStreaming(false);
          abortStreamRef.current = null;
          if (receivedAnyText && !chimePlayed) {
            chimePlayed = true;
            sfx.receive();
          }
        } else if (chunk.type === 'error') {
          setStreamingStatus(null);
          setIsStreaming(false);
          abortStreamRef.current = null;
          setMessagesByContext((prev) => {
            const list = prev[drawerAtSend] || [];
            return {
              ...prev,
              [drawerAtSend]: list.map((m) =>
                m.id === assistantMsgId ? (m.content ? m : { ...m, content: `_${chunk.message}_`, isError: true }) : m,
              ),
            };
          });
        }
      },
    });
  };

  const handleToolDecision = (messageId: string, confirmed: boolean) => {
    setMessagesByContext((prev) => {
      const messages = prev['chat'] || [];
      return {
        ...prev,
        ['chat']: messages.map((m) => {
          if (m.id === messageId) {
            return {
              ...m,
              toolStatus: confirmed ? 'executing' : 'denied',
              content: confirmed ? `Connecting to backend to ${m.toolName}…` : `Action "${m.toolName}" aborted.`,
            };
          }
          return m;
        }),
      };
    });

    if (confirmed) {
      setTimeout(() => {
        setMessagesByContext((prev) => {
          const messages = prev['chat'] || [];
          return {
            ...prev,
            ['chat']: messages.map((m) =>
              m.id === messageId
                ? { ...m, toolStatus: 'completed' as const, content: `Confirmed: **${m.toolName}** successful.` }
                : m,
            ),
          };
        });
      }, 3000);
    }
  };

  const handleStopStreaming = () => {
    if (abortStreamRef.current) abortStreamRef.current.cancelled = true;
    abortStreamRef.current = null;
    setIsStreaming(false);
  };

  const handleAction = async (id: string, decision: 'accept' | 'reject' | 'forward' | 'cancel') => {
    setDismissedActionIds((prev) => { const next = new Set(prev); next.add(id); return next; });
    if (decision !== 'accept' && decision !== 'reject') return;

    if (decision === 'reject') {
      track('approval', 'reject', { action_id: id });
      // Through execute-action so the requester gets a "declined" notification.
      const { data, error } = await supabase.functions.invoke('execute-action', { body: { action_id: id, decision: 'reject' } });
      if (error || !(data as { ok?: boolean } | null)?.ok) {
        log.warn('handleAction: reject failed', { id, err: error?.message });
        setDismissedActionIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
      }
      return;
    }

    track('approval', 'accept', { action_id: id });
    const { data, error } = await supabase.functions.invoke('execute-action', { body: { action_id: id } });
    if (error || !(data as { ok?: boolean } | null)?.ok) {
      log.warn('handleAction: execute-action failed', { id, err: error?.message });
      setDismissedActionIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
    }
  };

  const handleNewChat = () => {
    setMessagesByContext((prev) => ({ ...prev, ['general']: [] }));
    setCurrentChatId(null);
    newChatRef.current = null;
  };


  askHandlers.current = { newChat: handleNewChat, send: handleSendMessage };

  const handleOpenChat = async (id: string) => {
    if (isStreaming) return;
    const msgs = await chatHistory.open(id);
    setMessagesByContext((prev) => ({ ...prev, ['general']: msgs }));
    setCurrentChatId(id);
    newChatRef.current = null;
    setActiveDrawerId('chat');
    setIsLeftOpen(false);
  };

  const handleDeleteChat = async (id: string) => {
    if (!window.confirm('Delete this chat? This can\'t be undone.')) return;
    await chatHistory.remove(id);
    if (id === currentChatId) handleNewChat();
  };

  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden font-sans" style={{ background: c.bg, color: c.text }}>
      <AnimatePresence>
        {settings.isChaosMemeEnabled && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: Math.max(0, (settings.chaosLevel - 0.35) / 0.65) * 0.42 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
            className="absolute inset-0 pointer-events-none z-0 overflow-hidden"
          >
            <video src="/chaos.mp4" autoPlay loop muted playsInline className="w-full h-full object-cover" style={{ filter: 'contrast(1.05) saturate(0.85) blur(1px)' }} />
            <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/55 to-black/90" />
          </motion.div>
        )}
      </AnimatePresence>

      <TopBar
        venueName={venue.name}
        ownerName={user.name}
        isScrolled={isScrolled}
        onOpenLeft={() => setIsLeftOpen((p) => !p)}
        onOpenRight={() => { setIsRightOpen((p) => !p); void markNotifIdsRead(isManagerUser ? notifItems.filter((n) => !n.read_at && n.kind === 'approval_request').map((n) => n.id) : unreadRequestIds); }}
        showInboxWhenEmpty={!isManagerUser && requestNotifs.length > 0}
        onOpenTeam={() => { setTeamOpen((v) => !v); setTeamExpanded(false); }}
        teamUnread={teamUnread}
        teamOpen={teamOpen}
        onOpenNotifications={() => { setNotifOpen(new Set(bellUnreadIds)); void markNotifIdsRead(bellUnreadIds); }}
        pendingCount={isManagerUser ? visibleActions.filter((a) => a.status === 'pending').length : unreadRequestIds.length}
        notificationCount={bellUnreadIds.length}
        busyLevel={settings.chaosLevel}
      />

<div className="flex-1 flex overflow-hidden z-10" style={{ paddingTop: 'calc(3.5rem + env(safe-area-inset-top))' }}>
        <div className="hidden lg:block h-full backdrop-blur-md" style={{ background: c.isDark ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.06)' }}>
        <LeftSheet
          isOpen={true}
          onClose={() => {}}
          activeDrawerId={activeDrawerId}
          onSelectDrawer={(id) => { setActiveDrawerId(id); setIsLeftOpen(false); }}
          onNewChat={() => { handleNewChat(); setActiveDrawerId('chat'); }}
          history={chatHistory.available ? chatHistory : undefined}
          currentChatId={currentChatId}
          onOpenChat={(id) => void handleOpenChat(id)}
          onDeleteChat={(id) => void handleDeleteChat(id)}
          userRole={user.role}
          userName={user.name}
          userPosition={user.position ?? null}
          venueName={venue.name}
          canInvoices={canInvoices}
          onOpenSettings={(sec) => { setSettingsSection((sec as SettingsSection) ?? 'profile'); setActiveDrawerId('settings'); }}
          onSignOut={() => signOut()}
        />
        </div>

        <div className="lg:hidden">
          <LeftSheet
            isOpen={isLeftOpen}
            onClose={() => setIsLeftOpen(false)}
            activeDrawerId={activeDrawerId}
            onSelectDrawer={(id) => { setActiveDrawerId(id); setIsLeftOpen(false); }}
            onNewChat={() => { handleNewChat(); setActiveDrawerId('chat'); }}
            history={chatHistory.available ? chatHistory : undefined}
            currentChatId={currentChatId}
            onOpenChat={(id) => void handleOpenChat(id)}
            onDeleteChat={(id) => void handleDeleteChat(id)}
            userRole={user.role}
            userName={user.name}
          userPosition={user.position ?? null}
            venueName={venue.name}
            canInvoices={canInvoices}
            onOpenSettings={(sec) => { setSettingsSection((sec as SettingsSection) ?? 'profile'); setActiveDrawerId('settings'); }}
          onSignOut={() => signOut()}
          />
        </div>

              <main
        ref={scrollContainerRef}
        className="flex-1 overflow-hidden relative h-full flex flex-col"
        style={{ background: c.bg }}
        id="main-scroll-container"
      >
          <AnimatePresence mode="wait">
            <motion.div
              key={activeDrawerId}
              initial={{ opacity: 0, x: 28, filter: 'blur(6px)' }}
              animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, x: -28, filter: 'blur(6px)', transition: { duration: 0.2 } }}
              transition={{ duration: 0.38, ease: [0.23, 1, 0.32, 1] }}
              className="h-full flex flex-col overflow-hidden"
            >
              {activeDrawerId === 'dashboard' && !isManagerUser ? (
                <div className="flex-1 overflow-y-auto scrollbar-hide">
                  <StaffDashboard venueId={venue.id} userId={user.id} userName={user.name}
          timezone={venue.timezone} />
                </div>
              ) : activeDrawerId === 'dashboard' ? (
                <div className="flex-1 overflow-y-auto scrollbar-hide">
                  <OverviewPanel
                    venueId={venue.id}
                    userId={user.id}
                    userRole={user.role}
                    userName={user.name}
                    venueName={venue.name}
                    timezone={venue.timezone}
                    pendingActions={visibleActions.filter(a => a.status === 'pending')}
                    onOpenApprovals={user.role === 'owner' || user.role === 'manager' ? () => setIsRightOpen(true) : undefined}
                    onSelectSuggestion={(prompt) => {
                      setActiveDrawerId('chat');
                      setTimeout(() => handleSendMessage(prompt), 100);
                    }}
                    onOpenStock={() => setIsStockOpen(true)}
                    onOpenRoster={() => setIsRosterOpen(true)}
                    onOpenWastage={() => setIsWastageOpen(true)}
                    onOpenPO={() => setIsPOOpen(true)}
                    onOpenCount={() => setIsCountOpen(true)}
                    onOpenScan={() => setActiveDrawerId('invoices')}
                    onOpenRecipes={() => setIsRecipesOpen(true)}
                    onOpenReport={() => setIsDailyReportOpen(true)}
                    onOpenCogs={() => setIsCogsOpen(true)}
                    onOpenPrice={() => setIsPriceOpen(true)}
                  />
                </div>
              ) : activeDrawerId === 'financials' ? (
                <div className="flex-1 overflow-y-auto scrollbar-hide">
                  <FinancialsPage venueId={venue.id} userId={user.id} userRole={user.role} canInvoices={canInvoices} tab={finTab} onTab={setFinTab} />
                </div>
              ) : activeDrawerId === 'settings' ? (
                <div className="flex-1 overflow-y-auto scrollbar-hide">
                  <Suspense fallback={null}>
                    <SettingsPage section={settingsSection} onSection={setSettingsSection} venueId={venue.id} venueName={venue.name} orgId={orgId ?? null}
                      userId={user.id} userName={user.name ?? ''} userEmail={user.email} userRole={user.role}
                      onSignOut={() => signOut()} onOpenInvite={() => setIsInviteOpen(true)} onOpenEodQuestions={() => setIsEodQuestionsOpen(true)}
                      onOpenMemory={(t) => { setMemoryTab(t ?? 'personal'); setIsMemoryOpen(true); }}
                      integrationResult={integrationResult} onIntegrationHandled={() => setIntegrationResult(null)} />
                  </Suspense>
                </div>
              ) : activeDrawerId === 'documents' ? (
                <div className="flex-1 overflow-y-auto scrollbar-hide">
                  <DocumentsPage venueId={venue.id} canManage={user.role === 'owner' || user.role === 'manager'} userId={user.id} userName={user.name ?? ''}
                    openDocId={openDocId} onOpenedDoc={() => setOpenDocId(null)} pdfEdit={pdfEdit} onPdfEditOpened={() => setPdfEdit(null)} />
                </div>
              ) : activeDrawerId === 'tasks' ? (
                <div className="flex-1 overflow-y-auto scrollbar-hide">
                  <TasksPage venueId={venue.id} userId={user.id} userRole={user.role} preset={taskPreset} onPresetUsed={() => setTaskPreset(null)} />
                </div>
                  ) : activeDrawerId === 'training' ? (
                    <div className="flex-1 overflow-y-auto scrollbar-hide">
                      <TrainingPage venueId={venue.id} userId={user.id} userRole={user.role} openCourseId={openCourseId} onOpenedCourse={() => setOpenCourseId(null)} />
                    </div>
                  ) : activeDrawerId === 'eod' ? (
                    <div className="flex-1 overflow-y-auto scrollbar-hide">
                      <EodSection venueId={venue.id} venueName={venue.name} tz={venue.timezone ?? null}
                        userId={user.id} userName={user.name ?? ''} userRole={user.role} request={eodRequest} />
                    </div>
                  ) : (
                <Chat
                  venue={venueForChildren}
                  messages={currentMessages}
                  onSendMessage={handleSendMessage}
                  isStreaming={isStreaming}
                  streamingStatus={streamingStatus}
                  onStopStreaming={handleStopStreaming}
                  activeDrawerId={activeDrawerId}
                  onToolDecision={handleToolDecision}
                  onOpenPanel={undefined}
                  openPanelLabel={undefined}
                  userId={user.id}
                  userRole={user.role}
                  onNewChat={handleNewChat}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </main>

        <div className="hidden lg:block h-full backdrop-blur-md" style={{ background: c.isDark ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.06)' }}>
          {isRightOpen && (
            <RightSheet 
            isOpen={true} 
            onClose={() => setIsRightOpen(false)} 
            actions={visibleActions} 
            onAction={handleAction}
            venueId={venue.id}
            userId={user.id}
            isManager={user.role === 'owner' || user.role === 'manager'}
            requests={requestNotifs}
          />
          )}
        </div>
        {isRightOpen && (
          <button
            onClick={() => setIsRightOpen(false)}
            className="fixed top-16 right-4 z-50 w-8 h-8 flex items-center justify-center rounded-full transition-all active:scale-95"
            style={{ background: c.iconBg }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = c.isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = c.iconBg; }}
          >
            <X size={16} style={{ color: c.text }} />
          </button>
        )}

        <div className="lg:hidden">
          <RightSheet isOpen={isRightOpen} onClose={() => setIsRightOpen(false)} actions={visibleActions} onAction={handleAction} venueId={venue.id} userId={user.id} isManager={user.role === 'owner' || user.role === 'manager'}
            requests={requestNotifs} />
        </div>

        <Suspense fallback={null}>
        <TeamChatsPanel open={teamOpen} expanded={teamExpanded} onClose={() => { setTeamOpen(false); setTeamExpanded(false); }} onExpand={setTeamExpanded}
          venueId={venue.id} userId={user.id} userRole={user.role} tz={venue.timezone ?? 'Australia/Sydney'} />

          <MemoryModal key={`${memoryTab}-${isMemoryOpen}`} isOpen={isMemoryOpen} onClose={() => setIsMemoryOpen(false)} venueId={venue.id} userId={user.id} userRole={user.role} venueName={venue.name} initialTab={memoryTab} />

          <NotificationsSheet isOpen={!!notifOpen} onClose={() => setNotifOpen(null)} unread={notifOpen ?? new Set()}
            items={notifItems.filter((n) => !isInboxKind(n.kind) || n.kind === 'approval_request')}
            onMarkAll={() => { void markNotifIdsRead(notifItems.filter((n) => !n.read_at).map((n) => n.id)); setNotifOpen(new Set()); }}
            onClear={(ids) => { void clearNotifs(ids); setNotifOpen(new Set()); }}
            onOpenTasks={() => { setNotifOpen(null); setActiveDrawerId('tasks'); }}
            onOpenItem={(n) => {
              setNotifOpen(null);
              void markNotifIdsRead([n.id]);
              void import('./notifications-sheet').then(({ notificationTarget }) => {
                const t = notificationTarget(n);
                if (t) window.dispatchEvent(new CustomEvent('opsalo:navigate', { detail: t }));
              });
            }} />
          <TasksModal isOpen={isTasksOpen} onClose={() => setIsTasksOpen(false)} venueId={venue.id} userId={user.id} />

          {orgId && (
            <ApprovalRulesModal isOpen={isApprovalRulesOpen} onClose={() => setIsApprovalRulesOpen(false)} organizationId={orgId} canEdit={user.role === 'owner' || user.role === 'manager'} />
          )}

          {orgId && (
            <ToolPermissionsModal isOpen={isToolPermissionsOpen} onClose={() => setIsToolPermissionsOpen(false)} organizationId={orgId} canEdit={user.role === 'owner' || user.role === 'manager'} />
          )}

          <AuditLogModal isOpen={isAuditLogOpen} onClose={() => setIsAuditLogOpen(false)} venueId={venue.id} />
          <FilesModal isOpen={isFilesOpen} onClose={() => setIsFilesOpen(false)} venueId={venue.id} canManage={user.role === 'owner' || user.role === 'manager'} />
          <NotificationPrompt />
          <FileViewerHost />
          <TranslationHost />
          <StockModal isOpen={isStockOpen} onClose={() => setIsStockOpen(false)} venueId={venue.id} canManage={user.role === 'owner' || user.role === 'manager'} />
          <RosterModal isOpen={isRosterOpen} onClose={() => setIsRosterOpen(false)} venueId={venue.id} timezone={venue.timezone} />
          <WastageModal isOpen={isWastageOpen} onClose={() => setIsWastageOpen(false)} venueId={venue.id} />
          <PurchaseOrdersModal isOpen={isPOOpen} onClose={() => setIsPOOpen(false)} venueId={venue.id} canManage={user.role === 'owner' || user.role === 'manager'} />
          <StockCountModal isOpen={isCountOpen} onClose={() => setIsCountOpen(false)} venueId={venue.id} userId={user.id} />
          <InvoiceScanModal isOpen={isScanOpen} onClose={() => setIsScanOpen(false)} venueId={venue.id} />
          <RecipesModal isOpen={isRecipesOpen} onClose={() => setIsRecipesOpen(false)} venueId={venue.id} userId={user.id} canManage={user.role === 'owner' || user.role === 'manager'} />
          <DailyReportModal isOpen={isDailyReportOpen} onClose={() => setIsDailyReportOpen(false)} venueId={venue.id} />
          <CogsModal isOpen={isCogsOpen} onClose={() => setIsCogsOpen(false)} />

          <ContextModal
            isOpen={isContextOpen}
            onClose={() => setIsContextOpen(false)}
            canEdit={user.role === 'owner' || user.role === 'manager'}
            saving={venueContextSaving || drawerContextSaving}
            venueContext={venueContext}
            drawerContexts={drawerContexts}
            onSaveVenue={saveVenueContext}
            onSaveDrawer={saveDrawerContext}
          />

          <InviteModal isOpen={isInviteOpen} onClose={() => setIsInviteOpen(false)} venueId={venue.id} userId={user.id} />
          {user.role === 'owner' && (
            <EodQuestionsModal isOpen={isEodQuestionsOpen} onClose={() => setIsEodQuestionsOpen(false)}
              eodQuestions={eodQuestions} onSave={saveEodQuestions} />
          )}
          {orgId && (
            <TeamAccessModal isOpen={isTeamAccessOpen} onClose={() => setIsTeamAccessOpen(false)} orgId={orgId} venueId={venue.id} currentUserId={user.id} currentRole={user.role} canManage={user.role === 'owner' || user.role === 'manager'} />
          )}
          <OnboardingHost venueId={venue.id} venueName={venue.name} userId={user.id} userName={user.name ?? null} role={user.role}
            showNudge={activeDrawerId === 'chat' || activeDrawerId === 'dashboard'} />
          <PriceComparisonModal isOpen={isPriceOpen} onClose={() => setIsPriceOpen(false)} venueId={venue.id} userId={user.id} canManage={user.role === 'owner' || user.role === 'manager'} />
        </Suspense>
      </div>

      {activeDrawerId !== 'chat' && (
        <button
          onClick={() => setActiveDrawerId('chat')}
          className="hide-when-keyboard lg:hidden fixed bottom-20 left-4 z-[50] rounded-full shadow-lg transition-all active:scale-95"
          style={{ width: 52, height: 52, background: c.accentBg, border: `1px solid ${c.accentBorder}` }}
          aria-label="Back to chat"
        >
          <div className="w-full h-full flex items-center justify-center">
            <LogoMark size={28} color={c.accentSoft} />
          </div>
          <div
            className="absolute flex items-center gap-[2px] rounded-full"
            style={{ bottom: -4, right: -4, background: c.bgElevated, border: `1px solid ${c.accentBorder}`, padding: '3px 5px' }}
          >
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 1, repeat: Infinity, delay: i * 0.15, ease: 'easeInOut' }}
                className="rounded-full"
                style={{ width: 4, height: 4, background: c.accentSoft }}
              />
            ))}
          </div>
        </button>
      )}
    </div>
  );
}